import { useEffect, useRef, useState } from 'react';
import type { CwSyncError } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { formatDateTime } from '../../utils/date.js';
import { useCwDatasets, useCwSyncErrors, useClearCwSyncErrors } from './api.js';

interface CwSyncErrorLogProps {
  isAdmin: boolean;
}

interface ErrorRowProps {
  error: CwSyncError;
}

function ErrorRow({ error }: ErrorRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="blue">{error.datasetLabel}</Badge>
            <Badge
              tone={
                error.scope === 'sync' ? 'red' : error.scope === 'warning' ? 'gray' : 'amber'
              }
            >
              {error.scope === 'sync'
                ? 'Whole sync'
                : error.scope === 'warning'
                  ? 'Warning'
                  : 'Record'}
            </Badge>
            {error.recordLabel ? (
              <span className="truncate text-sm font-medium text-slate-700">
                {error.recordLabel}
              </span>
            ) : error.cwRecordId ? (
              <span className="text-sm font-medium text-slate-700">#{error.cwRecordId}</span>
            ) : null}
          </div>
          <p
            className={`mt-1 truncate text-sm ${
              error.scope === 'warning' ? 'text-amber-600' : 'text-rose-600'
            }`}
            title={error.message}
          >
            {error.message}
          </p>
          <p className="mt-1 text-xs text-slate-400">{formatDateTime(error.createdAt)}</p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Hide' : 'Investigate'}
        </Button>
      </div>

      {expanded ? (
        <div className="space-y-3 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Error</p>
            <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-rose-700 ring-1 ring-slate-200">
              {error.message}
            </pre>
          </div>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Dataset</dt>
              <dd className="mt-1 text-sm text-slate-800">{error.datasetLabel}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">CW record ID</dt>
              <dd className="mt-1 text-sm text-slate-800">{error.cwRecordId ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Scope</dt>
              <dd className="mt-1 text-sm text-slate-800">{error.scope}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Request ID</dt>
              <dd className="mt-1 break-all text-sm text-slate-800">{error.requestId ?? '—'}</dd>
            </div>
          </dl>
          {error.payloadJson ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                ConnectWise record
              </p>
              <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                {error.payloadJson}
              </pre>
            </div>
          ) : (
            <p className="text-xs text-slate-400">No record payload was captured for this error.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function CwSyncErrorLog({ isAdmin }: CwSyncErrorLogProps): JSX.Element | null {
  const datasetsQuery = useCwDatasets();
  const errorsQuery = useCwSyncErrors();
  const clearMutation = useClearCwSyncErrors();

  // The log is written only when a sync finishes, so refresh it once a background run
  // transitions from running to done to surface that run's failures (or clear them).
  const isRunning = (datasetsQuery.data ?? []).some((d) => d.lastOutcome === 'running');
  const wasRunning = useRef(isRunning);
  useEffect(() => {
    if (wasRunning.current && !isRunning) {
      void errorsQuery.refetch();
    }
    wasRunning.current = isRunning;
  }, [isRunning, errorsQuery]);

  if (!isAdmin) return null;

  const errors = errorsQuery.data ?? [];

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Sync error log</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Failures from the latest run of each dataset. Expand a row to investigate the cause.
          </p>
        </div>
        {errors.length > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isPending}
          >
            {clearMutation.isPending ? 'Clearing…' : 'Clear log'}
          </Button>
        ) : null}
      </div>

      {errorsQuery.isLoading ? (
        <p className="px-5 py-4 text-sm text-slate-500">Loading error log…</p>
      ) : errorsQuery.isError ? (
        <p className="px-5 py-4 text-sm text-rose-600">Failed to load the sync error log.</p>
      ) : errors.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No sync failures recorded.</p>
      ) : (
        <div>
          {errors.map((error) => (
            <ErrorRow key={error.id} error={error} />
          ))}
        </div>
      )}
    </div>
  );
}
